import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { ASSET_MEDIA, Disclosure, Iconify } from "../..";
import React, { useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslations } from "next-intl";

const StyledTextField = styled(OutlinedInput)(({ theme }) => ({
  borderRadius: "0.5rem",
  padding: theme.spacing(0.4, 1),
}));

interface AssetFilterModalProps {
  disclosure: Disclosure;
  assets: string[];
  onChange: (value: string[]) => void;
  value: string[];
}
export const AssetFilterModal = ({
  disclosure,
  assets,
  onChange,
  value,
}: AssetFilterModalProps) => {
  const [query, setQuery] = useState<string>("");
  const t = useTranslations();
  const theme = useTheme();

  const filteredOptions = useMemo(
    () =>
      assets.filter((asset) =>
        asset.toLowerCase().includes(query.toLowerCase()),
      ),
    [query, assets],
  );

  return (
    <Dialog
      sx={{ backgroundColor: "transparent" }}
      open={disclosure.isOpen}
      fullWidth={true}
      onClose={disclosure.onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.25)",
          },
        },
      }}
      PaperProps={{
        sx: {
          maxWidth: "25rem",
          boxShadow: "0px 25px 50px 0px rgba(0, 0, 0, 0.12)",
          borderRadius: "0.5rem",
          margin: { xs: "0.5rem", md: "1rem" },
          width: { xs: "calc(100% - 1rem)", md: "calc(100% - 32px)" },
        },
      }}
    >
      <Stack spacing={2} bgcolor="background.paper">
        <Stack
          spacing={1}
          p={{ xs: "1rem", md: "1.25rem 1.5rem 0.5rem 1.5rem" }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight="500" lineHeight={1} fontSize="1.25rem">
              {t("common.select-assets")}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={disclosure.onClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <StyledTextField
            autoFocus
            size="small"
            placeholder={t("common.select-token")}
            onChange={(e) => setQuery(e.target.value)}
            value={query}
            startAdornment={
              <InputAdornment position="start" sx={{ pl: "0.2rem" }}>
                <Iconify icon="iconamoon:search-light" fontSize="1.4rem" />
              </InputAdornment>
            }
            fullWidth
          />
        </Stack>
        <Divider />
        <Stack padding={theme.spacing(0, 3, 3, 3)} borderRadius="0.5rem">
          {filteredOptions.map((item, index) => {
            const media = ASSET_MEDIA[item];
            return (
              <React.Fragment key={item}>
                <ButtonBase
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 0.5,
                  }}
                  onClick={() => {
                    if (value.includes(item)) {
                      onChange(value.filter((asset) => asset !== item));
                      return;
                    }
                    onChange([...value, item]);
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="0.5rem"
                    width="100%"
                  >
                    {media.icon({ height: "1.5rem", width: "1.5rem" })}
                    <Stack spacing={0.4} alignItems="flex-start">
                      <Typography fontWeight="500" lineHeight={1.1}>
                        {item}
                      </Typography>
                      <Typography
                        fontWeight="500"
                        lineHeight={1.1}
                        fontSize="0.875rem"
                        sx={{ opacity: "0.5" }}
                      >
                        {item}
                      </Typography>
                    </Stack>
                    <Box flex={1} />
                    <Checkbox checked={value.includes(item)} />
                  </Box>
                </ButtonBase>
              </React.Fragment>
            );
          })}
          <Box
            width="100%"
            display="flex"
            justifyContent="center"
            onClick={() => onChange([])}
          >
            <Button>{t("common.clear-all")}</Button>
          </Box>
        </Stack>
      </Stack>
    </Dialog>
  );
};
